import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { INR, fmtDate, fmtRelative, todayIso } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { RefreshCw, ArrowRight } from "lucide-react";

const RANGES = [
  { key: "1w", label: "Last 1 week", days: 7 },
  { key: "2w", label: "Last 2 weeks", days: 14 },
  { key: "1m", label: "Last 1 month", days: 30 },
  { key: "custom", label: "Custom range", days: null },
];

function isoDaysAgo(days) {
  return new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
}

export default function Reports() {
  const nav = useNavigate();
  const { user } = useAuth();
  const financeOk = user?.finance_access || user?.role === "super_admin" || user?.role === "admin";
  const [revByClient, setRevByClient] = useState([]);
  const [prod, setProd] = useState([]);
  const [rangeKey, setRangeKey] = useState("1m");
  const [customFrom, setCustomFrom] = useState(isoDaysAgo(30));
  const [customTo, setCustomTo] = useState(todayIso());
  const [transfers, setTransfers] = useState([]);
  const [users, setUsers] = useState([]);
  const [xfFrom, setXfFrom] = useState("");
  const [xfTo, setXfTo] = useState("");
  const [xfActor, setXfActor] = useState("");

  const { start, end } = useMemo(() => {
    if (rangeKey === "custom") return { start: customFrom, end: customTo };
    const r = RANGES.find((x) => x.key === rangeKey);
    return { start: isoDaysAgo(r.days), end: todayIso() };
  }, [rangeKey, customFrom, customTo]);

  const loadRevenue = async () => {
    if (!financeOk) return;
    try { const r = await api.get("/reports/revenue-by-client"); setRevByClient(r.data); } catch (_) {}
  };
  const loadProd = async () => {
    try { const r = await api.get("/reports/employee-productivity", { params: { start, end } }); setProd(r.data); } catch (_) {}
  };
  const loadTransfers = async () => {
    try {
      const params = { action: "task.transfer", start, end, limit: 500 };
      if (xfFrom) params.from_user_id = xfFrom;
      if (xfTo) params.to_user_id = xfTo;
      if (xfActor) params.actor_id = xfActor;
      const r = await api.get("/activity", { params });
      setTransfers(r.data);
    } catch (_) {}
  };
  useEffect(() => { loadRevenue(); api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, [financeOk]);
  useEffect(() => { loadProd(); loadTransfers(); }, [start, end, xfFrom, xfTo, xfActor]);

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Insights</div>
        <h1 className="text-4xl font-black tracking-tight">Reports</h1>
      </div>

      <Tabs defaultValue={financeOk ? "revenue" : "productivity"}>
        <TabsList>
          {financeOk && <TabsTrigger value="revenue" data-testid="tab-revenue">Revenue by Client</TabsTrigger>}
          <TabsTrigger value="productivity" data-testid="tab-productivity">Team Productivity</TabsTrigger>
          <TabsTrigger value="transfers" data-testid="tab-transfers">Task Transfers</TabsTrigger>
        </TabsList>

        {financeOk && (
          <TabsContent value="revenue" className="mt-4">
            <div className="flex justify-end mb-3">
              <Button variant="outline" size="icon" onClick={loadRevenue} title="Refresh"><RefreshCw size={14} /></Button>
            </div>
            <div className="border border-border rounded-md overflow-hidden bg-card">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    <th className="px-3 py-2.5">Client</th>
                    <th className="px-3 py-2.5 text-right">Contract</th>
                    <th className="px-3 py-2.5 text-right">Received</th>
                    <th className="px-3 py-2.5 text-right">Outstanding</th>
                    <th className="px-3 py-2.5 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {revByClient.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No revenue yet.</td></tr>}
                  {revByClient.map((r) => (
                    <tr key={r.client_id} className="data-row border-t border-border cursor-pointer" onClick={() => nav(`/clients/${r.client_id}`)} data-testid={`rev-row-${r.client_id}`}>
                      <td className="px-3 py-2.5 font-semibold">{r.company_name}</td>
                      <td className="px-3 py-2.5 text-right font-mono">{INR(r.contract_value)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-emerald-600 dark:text-emerald-400">{INR(r.received)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-primary">{INR(r.outstanding)}</td>
                      <td className="px-3 py-2.5 text-muted-foreground"><ArrowRight size={14} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        )}

        <TabsContent value="productivity" className="mt-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex border border-border rounded-md overflow-hidden">
              {RANGES.map((r) => (
                <button
                  key={r.key}
                  data-testid={`prod-range-${r.key}`}
                  onClick={() => setRangeKey(r.key)}
                  className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest transition-colors ${rangeKey === r.key ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                >{r.label}</button>
              ))}
            </div>
            {rangeKey === "custom" && (
              <>
                <div className="flex items-center gap-1"><span className="font-mono text-[10px] uppercase text-muted-foreground">From</span><Input type="date" className="w-40" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} data-testid="prod-custom-from" /></div>
                <div className="flex items-center gap-1"><span className="font-mono text-[10px] uppercase text-muted-foreground">To</span><Input type="date" className="w-40" value={customTo} onChange={(e) => setCustomTo(e.target.value)} data-testid="prod-custom-to" /></div>
              </>
            )}
            <div className="ml-auto text-xs font-mono text-muted-foreground">
              {fmtDate(start)} → {fmtDate(end)}
            </div>
            <Button variant="outline" size="icon" onClick={loadProd} title="Refresh"><RefreshCw size={14} /></Button>
          </div>

          <div className="border border-border rounded-md overflow-hidden bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  <th className="px-3 py-2.5">Team member</th>
                  <th className="px-3 py-2.5 text-right">Completed</th>
                  <th className="px-3 py-2.5 text-right">Open</th>
                  <th className="px-3 py-2.5 text-right">Overdue</th>
                  <th className="px-3 py-2.5 text-right">Transferred out</th>
                  <th className="px-3 py-2.5 text-right">Received</th>
                  <th className="px-3 py-2.5 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {prod.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No productivity data.</td></tr>}
                {prod.map((r) => (
                  <tr key={r.id} className="data-row border-t border-border cursor-pointer" onClick={() => nav(`/tasks?assignee_id=${r.id}`)} data-testid={`prod-row-${r.id}`}>
                    <td className="px-3 py-2.5 font-semibold">{r.name}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{r.completed_this_month}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{r.open}</td>
                    <td className={`px-3 py-2.5 text-right font-mono ${r.overdue > 0 ? "text-destructive" : ""}`}>{r.overdue}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{r.transfers_out}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-muted-foreground">{r.transfers_in}</td>
                    <td className="px-3 py-2.5 text-muted-foreground"><ArrowRight size={14} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-xs text-muted-foreground">
            Row click → filters Tasks by that team member. "Transferred out/received" counts task reassignments in the period.
          </div>
        </TabsContent>

        <TabsContent value="transfers" className="mt-4 space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="inline-flex border border-border rounded-md overflow-hidden">
              {RANGES.map((r) => (
                <button
                  key={r.key}
                  data-testid={`xf-range-${r.key}`}
                  onClick={() => setRangeKey(r.key)}
                  className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest transition-colors ${rangeKey === r.key ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                >{r.label}</button>
              ))}
            </div>
            <Select value={xfFrom || "__all__"} onValueChange={(v) => setXfFrom(v === "__all__" ? "" : v)}>
              <SelectTrigger data-testid="xf-from-filter" className="w-48"><SelectValue placeholder="Transferred from" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Transferred from — anyone</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={xfTo || "__all__"} onValueChange={(v) => setXfTo(v === "__all__" ? "" : v)}>
              <SelectTrigger data-testid="xf-to-filter" className="w-48"><SelectValue placeholder="Transferred to" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Transferred to — anyone</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={xfActor || "__all__"} onValueChange={(v) => setXfActor(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-48"><SelectValue placeholder="Acted by" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Acted by — anyone</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="ml-auto text-xs font-mono text-muted-foreground">
              {fmtDate(start)} → {fmtDate(end)} · {transfers.length} transfers
            </div>
            <Button variant="outline" size="icon" onClick={loadTransfers} title="Refresh"><RefreshCw size={14} /></Button>
          </div>

          <div className="border border-border rounded-md overflow-hidden bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  <th className="px-3 py-2.5">When</th>
                  <th className="px-3 py-2.5">Task</th>
                  <th className="px-3 py-2.5">From</th>
                  <th className="px-3 py-2.5">To</th>
                  <th className="px-3 py-2.5">By</th>
                  <th className="px-3 py-2.5 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {transfers.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No transfers in this range.</td></tr>}
                {transfers.map((t) => (
                  <tr key={t.id} className="data-row border-t border-border cursor-pointer" onClick={() => nav(`/tasks?open=${t.entity_id}`)} data-testid={`xf-row-${t.id}`}>
                    <td className="px-3 py-2.5 font-mono text-xs">
                      <div>{fmtRelative(t.created_at)}</div>
                      <div className="text-[10px] text-muted-foreground">{fmtDate(t.created_at)}</div>
                    </td>
                    <td className="px-3 py-2.5 font-semibold">{t.entity_label || "Task"}</td>
                    <td className="px-3 py-2.5 font-mono text-xs">{t.meta?.from_name || "Unassigned"}</td>
                    <td className="px-3 py-2.5 font-mono text-xs font-semibold text-primary">{t.meta?.to_name || "—"}</td>
                    <td className="px-3 py-2.5 text-xs">{t.actor_name}</td>
                    <td className="px-3 py-2.5 text-muted-foreground"><ArrowRight size={14} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
